import * as THREE from "three";

/**
 * Elevated camera preset with limited player-controlled rotation/zoom and
 * a reset control (Master Spec §18). Presentation-only: nothing here ever
 * feeds back into algorithm/domain state, and camera framing is never part
 * of puzzle difficulty.
 */
const DEFAULT_POLAR = THREE.MathUtils.degToRad(64);
const DEFAULT_AZIMUTH = 0;
const DEFAULT_RADIUS = 14;
const MIN_RADIUS = 5;
const MAX_RADIUS = 14;
const MAX_AZIMUTH_OFFSET = THREE.MathUtils.degToRad(35);

export class CameraRig {
  readonly camera: THREE.PerspectiveCamera;

  private readonly canvas: HTMLCanvasElement;
  private readonly target = new THREE.Vector3(0, 1, -.5);
  private azimuth = DEFAULT_AZIMUTH;
  private radius = DEFAULT_RADIUS;
  private frameWidth = 10;
  setFrameWidth(width:number): void { this.frameWidth=width; this.reset(); }
  private fittedRadius(): number { return Math.max(DEFAULT_RADIUS, this.frameWidth / (2 * Math.tan(THREE.MathUtils.degToRad(22.5)) * this.camera.aspect) + 3); }
  private isDragging = false;
  private lastPointerX = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    this.applyPosition();

    this.canvas.addEventListener("pointerdown", this.onPointerDown);
    window.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp);
    this.canvas.addEventListener("wheel", this.onWheel, { passive: false });
  }

  setAspect(aspect: number): void {
    this.camera.aspect = aspect > 0 ? aspect : 1;
    this.camera.updateProjectionMatrix();
    this.reset();
  }

  reset(): void {
    this.azimuth = DEFAULT_AZIMUTH;
    this.radius = this.fittedRadius();
    this.applyPosition();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  update(_deltaSeconds: number): void {
    // Reserved for future easing/inertia. Intentionally a no-op in Phase 01.
  }

  dispose(): void {
    this.canvas.removeEventListener("pointerdown", this.onPointerDown);
    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
    this.canvas.removeEventListener("wheel", this.onWheel);
  }

  private onPointerDown = (event: PointerEvent): void => {
    this.isDragging = true;
    this.lastPointerX = event.clientX;
  };

  private onPointerMove = (event: PointerEvent): void => {
    if (!this.isDragging) return;
    const deltaX = event.clientX - this.lastPointerX;
    this.lastPointerX = event.clientX;
    this.azimuth = THREE.MathUtils.clamp(
      this.azimuth - deltaX * 0.005,
      DEFAULT_AZIMUTH - MAX_AZIMUTH_OFFSET,
      DEFAULT_AZIMUTH + MAX_AZIMUTH_OFFSET,
    );
    this.applyPosition();
  };

  private onPointerUp = (): void => {
    this.isDragging = false;
  };

  private onWheel = (event: WheelEvent): void => {
    event.preventDefault();
    this.radius = THREE.MathUtils.clamp(this.radius + event.deltaY * 0.01, Math.max(MIN_RADIUS,this.fittedRadius()*.8), Math.max(MAX_RADIUS,this.fittedRadius()*1.3));
    this.applyPosition();
  };

  private applyPosition(): void {
    const x = this.target.x + this.radius * Math.sin(DEFAULT_POLAR) * Math.sin(this.azimuth);
    const y = this.target.y + this.radius * Math.cos(DEFAULT_POLAR);
    const z = this.target.z + this.radius * Math.sin(DEFAULT_POLAR) * Math.cos(this.azimuth);
    this.camera.position.set(x, y, z);
    this.camera.lookAt(this.target);
  }
}
